import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  REGION_ORDER,
} from "../data/countries";
import { loadState } from "../state/storage";
import { getArcadeBest } from "../state/arcade";
import { isRegionCleared } from "../state/stats";
import type { ArcadeMode } from "../state/types";

interface ModeInfo {
  mode: ArcadeMode;
  title: string;
  blurb: string;
  scoreLabel: (score: number) => string;
}

const MODES: ModeInfo[] = [
  {
    mode: "speed-run",
    title: "Speed Run",
    blurb:
      "60 seconds, multiple choice. How many can you tap correctly before the clock runs out?",
    scoreLabel: (s) => `${s} correct in 60s`,
  },
  {
    mode: "survival",
    title: "Survival",
    blurb:
      "Type. Don't miss. One wrong answer ends the run. Track your longest streak.",
    scoreLabel: (s) => `${s} streak`,
  },
  {
    mode: "reverse",
    title: "Reverse",
    blurb:
      "Country name shown — tap the right country on the globe. 10 rounds.",
    scoreLabel: (s) => `${s} / 10`,
  },
];

const MIXED_REGION = "Mixed (cleared regions)";

export const mountArcadeHub = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  root.classList.add("view-arcade-hub");
  ctx.globe.setTargetIso(null);
  ctx.globe.setGuessIso(null);
  ctx.globe.setHeatmap(null);
  ctx.globe.setClickHandler(null);
  ctx.globe.setHoverEnabled(true);

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  const state = loadState();

  // Regions in the hub: every populated region, plus Mixed if any region
  // is cleared.
  const regionList: string[] = [];
  for (const r of REGION_ORDER) {
    if (grouped[r] && grouped[r].length > 0) regionList.push(r);
  }
  const anyCleared = regionList.some((r) =>
    isRegionCleared(
      state,
      (grouped[r] ?? []).map((c) => c.iso),
    ),
  );
  if (anyCleared) regionList.push(MIXED_REGION);

  const card = document.createElement("section");
  card.className = "arcade-card";
  card.innerHTML = `
    <header class="arcade-header">
      <button type="button" class="arcade-back" aria-label="Back to regions">← Back</button>
      <h1>Arcade</h1>
      <button type="button" class="arcade-leaderboard">🏆 Leaderboard</button>
    </header>
    <p class="arcade-subtitle">Timed and scored modes — best scores save per region.</p>
    <div class="arcade-modes"></div>
  `;
  root.appendChild(card);

  const back = card.querySelector(".arcade-back") as HTMLButtonElement;
  const leaderboardBtn = card.querySelector(".arcade-leaderboard") as HTMLButtonElement;
  const modesEl = card.querySelector(".arcade-modes") as HTMLDivElement;

  const listeners: Array<() => void> = [];

  for (const info of MODES) {
    const block = document.createElement("section");
    block.className = "arcade-mode";

    const header = document.createElement("header");
    header.className = "arcade-mode-header";
    header.innerHTML = `
      <h2>${info.title}</h2>
      <p>${info.blurb}</p>
    `;
    block.appendChild(header);

    const grid = document.createElement("ul");
    grid.className = "arcade-region-grid";
    grid.setAttribute("role", "list");

    for (const region of regionList) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "arcade-region-btn";
      const best = getArcadeBest(state, info.mode, region);
      const bestLabel = best
        ? `<span class="arcade-best">Best: ${info.scoreLabel(best.score)}</span>`
        : `<span class="arcade-best arcade-best-empty">No best yet</span>`;
      btn.innerHTML = `
        <span class="arcade-region-name">${region}</span>
        ${bestLabel}
      `;
      const handler = (): void => {
        ctx.nav.goArcadeMode({ mode: info.mode, region });
      };
      btn.addEventListener("click", handler);
      listeners.push(() => btn.removeEventListener("click", handler));
      li.appendChild(btn);
      grid.appendChild(li);
    }

    block.appendChild(grid);
    modesEl.appendChild(block);
  }

  const onBack = (): void => ctx.nav.goRegionPicker();
  const onLeaderboard = (): void => ctx.nav.goLeaderboard();
  back.addEventListener("click", onBack);
  leaderboardBtn.addEventListener("click", onLeaderboard);

  return () => {
    for (const off of listeners) off();
    back.removeEventListener("click", onBack);
    leaderboardBtn.removeEventListener("click", onLeaderboard);
    root.classList.remove("view-arcade-hub");
  };
};
